























































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';

/**
 * Mixins
 */
import mixinsAPI from "@/mixins/api-mixins";

/**
 * Helpers
 */
import { getFullName } from "@/utils/users-helpers";
import { smartDisplayMinutes, getShortTimeString, differenceInMinutesBetweenDates, getLocaleDateString, getElegantDateString, getMinDate, addMinutes } from "@/utils/date-helpers";

/**
 * Components
 */
import Dialog from "@/components/_global/Dialog.vue";

/**
 * API
 */
import bookingsAPI from "@/api/bookings";
import usersAPI from "@/api/users";

/**
 * Types
 */
import { CalendarTimedEvent } from './Calendar.vue';

/**
 * Composant de fenêtre modale de création d'une réservation horaire.
 */
@Component({ mixins: [mixinsAPI] })
export default class BookingCreationDialog extends Vue {
	$refs!: {
		modal: Dialog
	};

	/** Injections */
	getFullName = getFullName;
	smartDisplayMinutes = smartDisplayMinutes;
	getShortTimeString = getShortTimeString;
	getLocaleDateString = getLocaleDateString;
	getElegantDateString = getElegantDateString;

	/** VueX - Snackbar */
	@Action('openInfo', { namespace: 'snackbar' })
	openInfo: any;
	@Action('openError', { namespace: 'snackbar' })
	openError: any;

	/** Propriétés */
	@Prop({ default: 'fr-FR' })
	locale!: string;

	/** Attributs */
	isLoading = false;

	initialDate: Date | null = null;
	initialDurationInMinutes: number | null = null;
	initialUser: any | null = null;
	service: any | null = null;

	startTime: string | null = null;
	durationInMinutes: number | null = null;
	endTime: string | null = null;

	bookabilityMinimumDurationInMinutes: number | null = null;
	bookabilityMaximumDurationInMinutes: number | null = null;
	bookabilityStepInMinutes: number | null = null;

	isStartTimePickerOpen = false;

	// Pour l'autocomplete de l'usager à sélectionner
	autocompleteSelectedUser: any | null = null;
	search: any | null = null;
	isAutocompleteLoading: boolean = false;
	usersFetchedList: any[] = [];

	/**
	 * Date/heure de début de la réservation.
	 * Sert à l'enregistrement.
	 */
	get dateToBook(): Date {
		const dateToBook = new Date(this.initialDate as Date);
		if (this.startTime) {
			const hours = parseInt(this.startTime.split(':')[0], 10);
			const minutes = parseInt(this.startTime.split(':')[1], 10);
			dateToBook.setHours(hours);
			dateToBook.setMinutes(minutes);
		}
		return dateToBook;
	}

	/**
	 * Date/heure de fin de la réservation.
	 */
	get endDate(): Date {
		const dateToBook = new Date(this.initialDate as Date);
		if (this.endTime) {
			const hours = parseInt(this.endTime.split(':')[0], 10);
			const minutes = parseInt(this.endTime.split(':')[1], 10);
			dateToBook.setHours(hours);
			dateToBook.setMinutes(minutes);
		}
		return dateToBook;
	}

	/**
	 * Date/heure de fin du créneau horaire.
	 */
	get initialEndDate() {
		const endDate = new Date(this.initialDate as Date);
		const durationInMinutes = this.initialDurationInMinutes as number;
		endDate.setMinutes(endDate.getMinutes() + durationInMinutes);
		return endDate;
	}

	get minimumPickableTime() {
		return this.initialDate ? getShortTimeString(this.initialDate) : undefined;
	}
	get maximumPickableTime() {
		if (!this.initialEndDate) {
			return undefined;
		}
		let minimumBookableTime = 0;
		if (this.bookabilityMinimumDurationInMinutes) {
			minimumBookableTime = this.bookabilityMinimumDurationInMinutes;
		}
		const endDate = new Date(this.initialEndDate as Date);
		endDate.setMinutes(endDate.getMinutes() - minimumBookableTime);
		return getShortTimeString(endDate);
	}

	get allowedDurationsList() {
		if (!this.initialDate) {
			return [];
		}

		const maxPossibleDuration = differenceInMinutesBetweenDates(this.dateToBook, this.initialEndDate);
		const maxDuration = this.bookabilityMaximumDurationInMinutes ? Math.min(this.bookabilityMaximumDurationInMinutes, maxPossibleDuration) : maxPossibleDuration;

		const result = [];
		for (let i = 0; i <= maxDuration; ++i) {
			if (this.isDurationAllowed(i)) {
				result.push({ text: smartDisplayMinutes(i), value: i });
			}
		}
		return result;
	}

	get isDurationValid() {
		return this.bookabilityMinimumDurationInMinutes && this.durationInMinutes &&
			(this.bookabilityMinimumDurationInMinutes <= this.durationInMinutes) &&
			this.bookabilityMaximumDurationInMinutes && (this.durationInMinutes <= this.bookabilityMaximumDurationInMinutes);
	}

	get canSubmit() {
		return !this.isStartTimePickerOpen && this.isDurationValid;
	}

	get selectedUserID() {
		if (this.initialUser) {
			return this.initialUser.id;
		}
		else {
			return this.autocompleteSelectedUser;
		}
	}

	get usersList() {
		return this.usersFetchedList.map((x) => ({
			value: x.id,
			text: getFullName(x)
		}));
	}

	/** Méthodes */
	async open(event: CalendarTimedEvent, user: any) {
		this.initialDate = new Date(Date.parse(event.date));
		const hoursMinutesArray = event.time.split(':');
		this.initialDate.setHours(parseInt(hoursMinutesArray[0], 10));
		this.initialDate.setMinutes(parseInt(hoursMinutesArray[1], 10));
		this.initialDurationInMinutes = event.duration;
		this.initialUser = user;
		this.autocompleteSelectedUser = null;

		const customData = event.customData;
		this.service = customData.service;
		this.bookabilityMinimumDurationInMinutes = customData.bookabilityMinimumDurationInMinutes;
		this.bookabilityMaximumDurationInMinutes = customData.bookabilityMaximumDurationInMinutes;
		this.bookabilityStepInMinutes = customData.bookabilityStepInMinutes;

		this.startTime = getShortTimeString(this.initialDate);
		this.endTime = getShortTimeString(getMinDate([this.initialEndDate, this.getInitialMinimumEndDate()])); // Le minimum entre l'heure maximale et (l'heure initiale + durée minimale)

		this.$refs.modal.open();

	}
	close() {
		this.$refs.modal.close();
	}

	async handleSubmit() {
		this.isLoading = true;
		try {
			await bookingsAPI.createWithDuration({
				bookedDate: this.dateToBook,
				bookedDurationInMinutes: this.durationInMinutes,
				userID: this.selectedUserID,
				serviceID: this.service.id
			});
			this.$emit('onCreateSuccess');
			this.isLoading = false;
			this.openInfo(this.$t('snackbar.success'));
			this.close();

		}
		catch (ex) {
			(this as any).handleAPIError(ex);
			this.isLoading = false;
		}
	}

	/**
	 * La date de fin sélectionnée par défaut.
	 */
	getInitialMinimumEndDate() {
		const endDate = new Date(this.initialDate as Date);
		const durationInMinutes = this.bookabilityMinimumDurationInMinutes as number;
		endDate.setMinutes(endDate.getMinutes() + durationInMinutes);
		return endDate;
	}

	allowedMinutesStart(m: number) {
		// console.log(m, this.bookabilityStepInMinutes ? (m - (this.initialDate as Date).getMinutes()) % this.bookabilityStepInMinutes === 0 : true);
		return this.bookabilityStepInMinutes ? m % this.bookabilityStepInMinutes === 0 : true;
	}
	allowedMinutesEnd(m: number) {
		return this.bookabilityStepInMinutes
			? (m - (this.initialDate as Date).getMinutes() + (this.bookabilityMinimumDurationInMinutes as number)) % this.bookabilityStepInMinutes === 0
			: true;
	}

	/**
	 * La durée *d* est-elle autorisée à être sélectionnée.
	 */
	isDurationAllowed(d: number) {
		if (this.bookabilityMinimumDurationInMinutes && this.bookabilityMaximumDurationInMinutes
			&& !(this.bookabilityMinimumDurationInMinutes <= d && d <= this.bookabilityMaximumDurationInMinutes)) {
			return false;
		}
		return this.bookabilityStepInMinutes ? (d + (this.bookabilityMinimumDurationInMinutes as number)) % this.bookabilityStepInMinutes === 0 : true;
	}

	/** Watchers */
	@Watch('startTime')
	startTimeWatcher(val: string, oldVal: string) {
		if (this.allowedDurationsList.length > 0) {
			this.durationInMinutes = this.allowedDurationsList[0].value;
			this.inputDurationInMinutesWatcher(this.durationInMinutes);
		}
	}

	// @Watch('endTime')
	// endTimeWatcher(val: string, oldVal: string) {
	// 	this.durationInMinutes = this.durationInMinutes;
	// 	this.inputDurationInMinutesWatcher(this.durationInMinutes);
	// }

	@Watch('durationInMinutes')
	inputDurationInMinutesWatcher(val: number) {
		if (this.startTime) {
			this.endTime = addMinutes(this.startTime, val);
		}
	}

	@Watch('search')
	async searchChanged(val: string) {
		// if (this.isAutocompleteLoading) {
		// 	return;
		// }
		this.isAutocompleteLoading = true;
		try {
			this.usersFetchedList = await usersAPI.getAll(val);
			this.isAutocompleteLoading = false;
		}
		catch (ex) {
			this.isAutocompleteLoading = false;
		}
	}
}
